import { useState } from "react";
import useApiPost from "../../../hooks/PostData";
import { useChatLabels } from "../../../store/api/useChatLabels";
import { ChatLabel } from "../../../types/ChatListType";
import { useAppSelector } from "../../../utils/hooks";
import toast from "react-hot-toast";
import { useQueryClient } from "react-query";
import { useTheme } from "../../../context/ThemeProvider";

interface ChatLabelsModalProps {
  chatId: number;
  isOpen: boolean;
  onClose: () => void;
  currentLabels?: ChatLabel[];
}

export default function ChatLabelsModal({
  chatId,
  isOpen,
  onClose,
  currentLabels = [],
}: ChatLabelsModalProps) {
  const { postData } = useApiPost();
  const { data: labels, isLoading, invalidate } = useChatLabels();
  const user_type = useAppSelector((state) => state.userData.user_type);
  const queryClient = useQueryClient();
  const { theme } = useTheme();
  const [newLabelText, setNewLabelText] = useState("");
  const [newLabelColor, setNewLabelColor] = useState("#FF5733");

  const isBusiness = user_type === "business";

  const handleAddLabel = async () => {
    if (!newLabelText.trim() || !isBusiness) return;
    try {
      const res = await postData("chat_lable/add", {
        lable_text: newLabelText.trim(),
        lable_color: newLabelColor,
      });
      if (res?.status) {
        toast.success("Label added");
        invalidate();
        setNewLabelText("");
      } else {
        toast.error(res?.message || "Failed to add label");
      }
    } catch {
      toast.error("Failed to add label");
    }
  };

  const handleApplyOrRemove = async (
    slot: 1 | 2 | 3,
    lableId?: number,
    remove?: boolean
  ) => {
    try {
      const payload: Record<string, unknown> = { chat_id: chatId };
      if (remove) {
        payload[`remove_lable_${slot}`] = "true";
      } else if (lableId) {
        payload[`chat_lable_id_${slot}`] = lableId;
      }
      const res = await postData("chat_lable/apply_to_chat", payload);
      if (res?.status) {
        toast.success(remove ? "Label removed" : "Label applied");
        invalidate();
        queryClient.invalidateQueries(["chat_list"]);
        onClose();
      } else {
        toast.error(res?.message || "Failed");
      }
    } catch {
      toast.error("Failed");
    }
  };

  if (!isOpen) return null;

  const slots = [1, 2, 3] as const;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className={`max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl p-5 ${
          theme === "dark" ? "bg-gray-800" : "bg-white"
        }`}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Chat Labels</h3>
          <button onClick={onClose} className="text-xl hover:opacity-70">
            ×
          </button>
        </div>

        {isBusiness && (
          <div className="mb-4 flex gap-2">
            <input
              type="text"
              placeholder="New label text"
              value={newLabelText}
              onChange={(e) => setNewLabelText(e.target.value)}
              className="flex-1 rounded-lg border px-3 py-2 text-sm"
            />
            <input
              type="color"
              value={newLabelColor}
              onChange={(e) => setNewLabelColor(e.target.value)}
              className="h-10 w-12 cursor-pointer rounded border"
            />
            <button
              onClick={handleAddLabel}
              className="primary-gradient rounded-lg px-4 py-2 text-sm font-medium"
            >
              Add
            </button>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <span className="text-sm font-medium">Slots (max 3 labels per chat):</span>
            {isLoading ? (
              <div className="py-4 text-sm text-gray-500">Loading...</div>
            ) : (
              <div className="mt-2 space-y-3">
                {slots.map((slot) => {
                  const current = currentLabels[slot - 1];
                  return (
                    <div key={slot} className="flex items-center gap-2">
                      <span className="w-16 text-sm">Slot {slot}:</span>
                      {current ? (
                        <div className="flex items-center gap-2">
                          <span
                            className="rounded-full px-3 py-1 text-xs text-white"
                            style={{ backgroundColor: current.lable_color }}
                          >
                            {current.lable_text || "Label"}
                          </span>
                          {isBusiness && (
                            <button
                              onClick={() => handleApplyOrRemove(slot, undefined, true)}
                              className="text-xs text-red-500 hover:underline"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {labels.map((label) => (
                            <button
                              key={label.lable_id}
                              onClick={() =>
                                isBusiness && handleApplyOrRemove(slot, label.lable_id)
                              }
                              disabled={!isBusiness}
                              className="rounded-full px-3 py-1 text-xs text-white"
                              style={{ backgroundColor: label.lable_color }}
                            >
                              {label.lable_text || "Label"}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
